import ArrowForwardIosIcon from '@mui/icons-material/ArrowForward';
import {
    Avatar,
    Box,
    Divider,
    Skeleton,
    SxProps,
    Theme,
    Typography,
} from '@mui/material';
import { t } from 'i18next';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import quickbooksLogo from 'shared/assets/images/invoicing/quickbooks.png';
import xeroLogo from 'shared/assets/images/invoicing/xero.png';
import Database from 'shared/db/services/Database.web';
import Invoice from 'shared/db/services/Invoice';
import {
    ACCOUNTING_PROVIDERS,
    AccountingProviderType,
} from 'shared/types/Invoicing';

import { InvoiceIcon } from '@/assets/svg';
import BackLinkButton from '@/components/BackLinkButton';
import { useImagesContext } from '@/context/ImagesContext';
import { useInvoicingSystemContext } from '@/context/InvoicingSystemContext';
import { useAccountingProvider } from '@/hooks';
import { ROUTE } from '@/router/routes';
import { databaseValidation } from '@/services/databaseValidation';
import { dbServiceLogger } from '@/services/logger/logger';
import { COLOR, ENTITY_COLOR } from '@/theme/colors';
import { formatDate } from '@/utils/date';
import { getRoutePath } from '@/utils/router';

import InvoicingNotAvailableSection from '../InvoicesPage/components/InvoicingNotAvailableSection';

type ArchivedProviderData = {
    type: AccountingProviderType;
    name: string;
    count: number;
    notSynced: number;
    accountId: string;
    disconnectedAt?: Date;
    lastUsedAt?: Date;
};

const PROVIDER_NAMES: Record<AccountingProviderType, string> = {
    xero: 'Xero',
    quick_books: 'Quickbooks',
    internal: 'EQUINET Invoicing',
};

const PROVIDER_LOGOS: Record<AccountingProviderType, string | null> = {
    xero: xeroLogo,
    quick_books: quickbooksLogo,
    internal: null,
};

const pageStyles: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    px: 3,
    pt: 2,
};

function maskEmail(email: string): string {
    if (!email || !email.includes('@')) return email;
    const [beforeAt, afterAt] = email.split('@');
    const [beforeDot, afterDot] = afterAt?.split('.') ?? [afterAt];
    const maskedBefore = `${beforeAt.slice(
        0,
        Math.min(beforeAt.length - 1, 3),
    )}***`;
    const maskedDomain = beforeDot
        ? `${beforeDot.slice(0, Math.min(beforeDot.length - 1, 3))}***`
        : '';
    return `${maskedBefore}@${maskedDomain}${afterDot ? `.${afterDot}` : ''}`;
}

function ProviderLogo({ type }: { type: AccountingProviderType }) {
    const logo = PROVIDER_LOGOS[type];

    if (logo) {
        return (
            <Avatar
                alt={PROVIDER_NAMES[type]}
                src={logo}
                sx={{ width: 48, height: 48 }}
            />
        );
    }

    return (
        <Box
            sx={{
                width: 51,
                height: 51,
                borderRadius: '8px',
                backgroundColor: ENTITY_COLOR.invoices,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <InvoiceIcon color="#fff" width={24} height={24} />
        </Box>
    );
}

function ArchivedInvoicesPage() {
    const { ImagesService } = useImagesContext();
    const navigate = useNavigate();
    const accountingProvider = useAccountingProvider();
    const { isInvoicingAvailable, isInvoiceSystemEnabled } =
        useInvoicingSystemContext();

    const [providers, setProviders] = useState<ArchivedProviderData[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    const connectedProvider: AccountingProviderType | null =
        accountingProvider ?? (isInvoiceSystemEnabled ? 'internal' : null);

    const archivedProviders = useMemo(
        () => ACCOUNTING_PROVIDERS.filter((p) => p !== connectedProvider),
        [connectedProvider],
    );

    const invoiceService = useMemo(
        () =>
            new Invoice({
                database: Database.getDatabase(),
                imageService: ImagesService,
                logger: dbServiceLogger,
                validation: databaseValidation,
            }),
        [ImagesService],
    );

    useEffect(() => {
        let active = true;

        async function fetchData() {
            setIsLoading(true);
            try {
                const details = await invoiceService.getArchivedInvoicesDetails(
                    archivedProviders,
                );

                if (!active) return;

                const providerList: ArchivedProviderData[] = [];
                for (const providerType of archivedProviders) {
                    const data = details[providerType];
                    if (data && data.count > 0) {
                        providerList.push({
                            type: providerType,
                            name: PROVIDER_NAMES[providerType],
                            count: data.count,
                            notSynced: data.notSynced,
                            accountId: data.accountId,
                            disconnectedAt: data.disconnectedAt,
                            lastUsedAt: data.lastUsedAt,
                        });
                    }
                }

                setProviders(providerList);
            } finally {
                if (active) setIsLoading(false);
            }
        }

        fetchData();
        return () => {
            active = false;
        };
    }, [invoiceService, archivedProviders]);

    const totalCount = useMemo(
        () => providers.reduce((sum, p) => sum + p.count, 0),
        [providers],
    );

    const handleProviderClick = useCallback(
        (provider: AccountingProviderType) => {
            const route = getRoutePath(ROUTE.archivedInvoicesList, {
                provider,
            });
            navigate(route, {
                state: { totalCount },
            });
        },
        [navigate, totalCount],
    );

    if (!isInvoicingAvailable) {
        return <InvoicingNotAvailableSection />;
    }

    return (
        <Box sx={pageStyles}>
            <BackLinkButton
                text={t('Entities:invoices', { defaultValue: 'Invoices' })}
                to={ROUTE.invoices}
                testID="ArchivedInvoicesPage-BackButton"
            />
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 2,
                    mb: 2,
                }}
            >
                <InvoiceIcon
                    color={ENTITY_COLOR.invoices}
                    width={28}
                    height={28}
                />
                <Typography fontSize={22} fontWeight={700}>
                    {t('Invoicing:archived_invoices', {
                        defaultValue: 'Archived Invoices',
                    })}
                    {!isLoading ? ` (${totalCount})` : ''}
                </Typography>
            </Box>
            <Divider />
            {isLoading ? (
                <Box sx={{ mt: 3, maxWidth: 500 }}>
                    <Skeleton width="100%" height={80} variant="rectangular" />
                    <Skeleton
                        width="100%"
                        height={80}
                        variant="rectangular"
                        sx={{ mt: 2 }}
                    />
                </Box>
            ) : (
                providers.map((provider) => (
                    <Box key={provider.type} sx={{ maxWidth: 500 }}>
                        <Box
                            className="transition-transform hover:scale-[1.01] hover:cursor-pointer hover:bg-blue-50/50"
                            onClick={() => handleProviderClick(provider.type)}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 3,
                                px: 1,
                                borderRadius: '8px',
                            }}
                        >
                            <Box>
                                <Typography
                                    fontSize={18}
                                    fontWeight={700}
                                    sx={{ mb: 0.5 }}
                                >
                                    {provider.name}
                                </Typography>
                                {provider.type !== 'internal' && (
                                    <Typography
                                        fontSize={13}
                                        color={COLOR.paleSky}
                                    >
                                        {`Account ID: ${maskEmail(
                                            provider.accountId,
                                        )}`}
                                    </Typography>
                                )}
                                <Typography fontSize={13} color={COLOR.paleSky}>
                                    {provider.type === 'internal'
                                        ? `${t('Invoicing:last_used_on', {
                                              defaultValue: 'Last used on',
                                          })} ${
                                              provider.lastUsedAt
                                                  ? formatDate(
                                                        provider.lastUsedAt.toISOString(),
                                                    )
                                                  : '-'
                                          }`
                                        : `${t('Invoicing:disconnected_on', {
                                              defaultValue: 'Disconnected on',
                                          })} ${
                                              provider.disconnectedAt
                                                  ? formatDate(
                                                        provider.disconnectedAt.toISOString(),
                                                    )
                                                  : '-'
                                          }`}
                                </Typography>
                                <Typography fontSize={13} color={COLOR.paleSky}>
                                    {provider.notSynced > 0
                                        ? `${t('Invoicing:not_synced', {
                                              defaultValue: 'Not synced',
                                          })}: ${provider.notSynced}`
                                        : t(
                                              'Invoicing:all_invoices_synced_short',
                                              {
                                                  defaultValue:
                                                      'All invoices synchronised',
                                              },
                                          )}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                    }}
                                >
                                    <Typography
                                        fontSize={16}
                                        fontWeight={700}
                                        color={COLOR.skyBlue}
                                    >
                                        {provider.count}
                                    </Typography>
                                    <ArrowForwardIosIcon
                                        sx={{
                                            width: 14,
                                            height: 14,
                                            color: COLOR.skyBlue,
                                        }}
                                    />
                                </Box>
                                <ProviderLogo type={provider.type} />
                            </Box>
                        </Box>
                        <Divider />
                    </Box>
                ))
            )}
        </Box>
    );
}

export default ArchivedInvoicesPage;
